// Vista guiada del inspector (2026-09-09) — tablet-first: una sola columna,
// targets grandes, sin nada de lo que el resto del HCE necesita (nav,
// buscador, ficha de paciente). El inspector nunca vio un login: llegó acá
// por QR o PIN (AccesoAuditoriaPage), con una sesión que vive en
// sessionStorage y muere sola al cerrar la pestaña o al vencer.
import type { JSX } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cerrarSesionAuditor, leerSesionAuditor } from '@/auth/auditorSession';
import { cerrarVisitaAuditoria, obtenerChecklistAuditoria, verificarPacienteAuditoria, type ChecklistAuditoria, type VerificacionPaciente } from '@/lib/hceAuditoria';
import { ChecklistItems } from './ChecklistItems';
import { VisorAccesos } from './VisorAccesos';

/**
 * "Verificar un paciente puntual" (Modo Auditor sección C, 2026-09-10) — el
 * checklist nunca identifica pacientes al auditor (agregados solamente),
 * pero si tiene motivo real para revisar un caso (una queja, un expediente
 * físico) su token debe servirle para confirmarlo, por cédula exacta, no
 * navegando un listado de todos los pacientes.
 */
function VerificarPaciente({ token }: { token: string }): JSX.Element {
  const { t } = useTranslation();
  const [cedula, setCedula] = useState('');
  const [buscando, setBuscando] = useState(false);
  const [resultado, setResultado] = useState<VerificacionPaciente | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function buscar(): Promise<void> {
    if (!cedula.trim()) return;
    setBuscando(true);
    setError(null);
    setResultado(null);
    try {
      setResultado(await verificarPacienteAuditoria(token, cedula.trim()));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
    setBuscando(false);
  }

  return (
    <div id="verificar-paciente" className="rounded-lg border border-border bg-hce-surface p-3 space-y-2.5 scroll-mt-16">
      <div>
        <p className="text-sm font-medium text-hce-ink">{t('auditoria.verificarPaciente.titulo')}</p>
        <p className="text-xs text-hce-muted mt-0.5">{t('auditoria.verificarPaciente.descripcion')}</p>
      </div>
      <div className="flex gap-2">
        <Input
          value={cedula}
          onChange={(e) => setCedula(e.target.value)}
          placeholder={t('auditoria.verificarPaciente.placeholder')}
          onKeyDown={(e) => e.key === 'Enter' && void buscar()}
        />
        <Button onClick={() => void buscar()} disabled={buscando || !cedula.trim()}>
          {buscando ? t('auditoria.verificarPaciente.buscando') : t('auditoria.verificarPaciente.buscar')}
        </Button>
      </div>
      {error && (
        <Alert variant="red">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {resultado && (
        <dl className="space-y-1 border-t border-border pt-2 text-sm">
          {(
            [
              [t('auditoria.verificarPaciente.nombre'), resultado.nombre],
              [t('auditoria.verificarPaciente.cedula'), resultado.idNumber],
              [t('auditoria.verificarPaciente.archivo'), t(`auditoria.verificarPaciente.estado.${resultado.archivoEstado}`)],
              [t('auditoria.verificarPaciente.ultimaAtencion'), resultado.ultimaAtencion ?? '—'],
              [t('auditoria.verificarPaciente.consentimiento'), resultado.tieneConsentimiento ? t('auditoria.verificarPaciente.si') : t('auditoria.verificarPaciente.no')],
            ] as const
          ).map(([label, valor]) => (
            <div key={label} className="flex items-baseline justify-between gap-3">
              <dt className="text-hce-muted">{label}</dt>
              <dd className="font-mono text-hce-ink text-right">{valor}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

export function ChecklistAuditoriaPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // `useState(() => ...)` (inicializador perezoso), NO una llamada directa en
  // el cuerpo del componente — `leerSesionAuditor()` arma un objeto NUEVO
  // cada vez que se llama. Bug real encontrado en vivo 2026-09-10: con
  // `sesion` recalculado en cada render y puesto en las dependencias del
  // useEffect de abajo, cada carga de checklist disparaba un re-render, que
  // creaba una `sesion` con otra identidad, que volvía a disparar el efecto
  // — un loop infinito que mandó ~1 request/segundo al backend y contaminó
  // el registro de accesos con miles de filas falsas (26.372 en 30 días).
  const [sesion] = useState(() => leerSesionAuditor());

  const [checklist, setChecklist] = useState<ChecklistAuditoria | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cerrando, setCerrando] = useState(false);
  const [cerrada, setCerrada] = useState(false);

  const cargar = useCallback(async () => {
    if (!sesion) return;
    setCargando(true);
    setError(null);
    try {
      setChecklist(await obtenerChecklistAuditoria(sesion.token));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auditoria.errorChecklist'));
    } finally {
      setCargando(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!sesion) {
      void navigate('/auditoria/acceso', { replace: true });
      return;
    }
    void cargar();
  }, [sesion, navigate, cargar]);

  async function cerrarVisita(): Promise<void> {
    if (!sesion) return;
    setCerrando(true);
    setError(null);
    try {
      await cerrarVisitaAuditoria(sesion.token, sesion.sesionId);
      setCerrada(true);
      cerrarSesionAuditor();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auditoria.errorChecklist'));
    } finally {
      setCerrando(false);
    }
  }

  if (!sesion) return <div className="min-h-screen bg-hce-paper" />;

  return (
    <div className="min-h-screen bg-hce-paper">
      <div className="sticky top-0 z-10 bg-hce-surface border-b border-border px-4 py-3">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <h1 className="text-sm font-medium text-hce-ink">{t('auditoria.tituloClinica')}</h1>
          {!cerrada && (
            <span className="text-xs font-mono text-hce-muted">{t('auditoria.tiempoRestante', { hora: new Date(sesion.exp).toLocaleTimeString('es-EC') })}</span>
          )}
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-5 space-y-4">
        {cerrada ? (
          <Alert>
            <AlertDescription>{t('auditoria.constanciaLista')}</AlertDescription>
          </Alert>
        ) : (
          <>
            <p className="text-sm text-hce-muted">{t('auditoria.checklistDescripcion')}</p>

            {error && (
              <Alert variant="red">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {cargando && !checklist ? (
              <p className="text-sm text-hce-muted">{t('auditoria.cargando')}</p>
            ) : checklist ? (
              <>
                <ChecklistItems checklist={checklist} token={sesion.token} />
                <VerificarPaciente token={sesion.token} />
                <VisorAccesos token={sesion.token} esAuditor />
              </>
            ) : null}

            <Button onClick={() => void cerrarVisita()} disabled={cerrando || !checklist} className="w-full" size="lg">
              {cerrando ? t('auditoria.cerrandoVisita') : t('auditoria.cerrarVisita')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
